// src/components/pages/events/EventWatchPage.js
// Path: src/components/pages/events/EventWatchPage.js

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "styled-components";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../../firebase";
import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { getUserRental, isRentalActive } from "../../../services/rentalService";
import CinematicPlayer from "../../molecules/Video/CinematicPlayer";

// --- helpers ---
const allowedVideoHosts = ["firebasestorage.googleapis.com", "swayer.blob.core.windows.net"];
function isAllowedVideoUrl(url) {
  try {
    const u = new URL(url);
    return allowedVideoHosts.includes(u.hostname);
  } catch {
    return false;
  }
}
function buildAzureMainMp4(submissionSaleId) {
  if (!submissionSaleId) return null;
  return `https://swayer.blob.core.windows.net/${submissionSaleId}-pub/_main.mp4`;
}

const EventWatchPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [event, setEvent] = useState(location.state?.event || null);
  const [rental, setRental] = useState(null);
  const [allowed, setAllowed] = useState(false);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null); // currently playing

  useEffect(() => {
    let canceled = false;

    const load = async (uid) => {
      setLoading(true);
      try {
        // Event
        let ev = location.state?.event;
        if (!ev) {
          const snap = await getDoc(doc(db, "events", eventId));
          if (!snap.exists()) {
            navigate(`/challenge/events/${eventId}`);
            return;
          }
          ev = { id: snap.id, ...snap.data() };
        }
        if (canceled) return;
        setEvent(ev);

        // Rental gate
        const r = await getUserRental(uid, ev.id);
        if (canceled) return;
        setRental(r);

        const ok = isRentalActive(r);
        setAllowed(ok);
        if (!ok) {
          navigate(`/challenge/events/${eventId}`);
          return;
        }

        // Content, newest first for “playlist”
        let cSnap;
        try {
          cSnap = await getDocs(query(collection(db, "events", ev.id, "content"), orderBy("createdAt", "desc")));
        } catch {
          cSnap = await getDocs(collection(db, "events", ev.id, "content"));
        }

        const derived = [];
        cSnap.forEach((d) => {
          const data = d.data();
          const isManual = !!data.isManual;

          const creator = isManual ? data.creator || null : data?.profileData?.displayName || null;
          const title = data.title || d.id;
          const description = data.description || "";

          const thumb =
            data?.thumbnail?.url ||
            (data?.submissionSaleId
              ? `https://swayer.blob.core.windows.net/${data.submissionSaleId}-pub/_thumb_large.jpg`
              : null);

          let videoUrl = null;
          if (isManual) {
            if (data?.fullVideo?.url && isAllowedVideoUrl(data.fullVideo.url)) {
              videoUrl = data.fullVideo.url;
            }
          } else {
            const candidate = buildAzureMainMp4(data?.submissionSaleId);
            if (candidate && isAllowedVideoUrl(candidate)) {
              videoUrl = candidate;
            }
          }

          const teaserUrl = data?.teaserVideo?.url || null;

          derived.push({
            id: d.id,
            isManual,
            title,
            description,
            creator,
            thumb,
            videoUrl,
            teaserUrl,
            _raw: data,
          });
        });

        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.groupCollapsed(`[EventWatchPage] event ${ev.id} derived playlist`);
          // eslint-disable-next-line no-console
          console.log("Derived items:", derived);
          // eslint-disable-next-line no-console
          console.groupEnd();
        }

        // pick first playable as default
        const firstPlayable = derived.find((x) => !!x.videoUrl) || derived[0] || null;
        setItems(derived);
        setSelected(firstPlayable || null);
      } finally {
        if (!canceled) setLoading(false);
      }
    };

    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        navigate(`/challenge/events/${eventId}`);
        return;
      }
      load(u.uid);
    });

    return () => {
      canceled = true;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const expiresAt = useMemo(() => {
    if (!rental?.accessEnd) return null;
    return rental.accessEnd?.toDate ? rental.accessEnd.toDate() : new Date(rental.accessEnd);
  }, [rental]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh" }}>
          <CircularProgress sx={{ color: theme.colors.red }} />
        </Box>
      </Container>
    );
  }

  if (!allowed) return null;

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 2 }}>
        <Typography variant="h4" sx={{ color: "white", fontFamily: "basic-sans", fontWeight: 800 }}>
          {event?.title || "Your rental"}
        </Typography>
        {expiresAt && (
          <Alert
            severity="info"
            sx={{ bgcolor: theme.colors.darkBlue, color: "white", border: `1px solid ${theme.colors.palePurple}` }}
          >
            Access until {expiresAt.toLocaleString()}
          </Alert>
        )}
      </Box>

      {/* Inline Cinematic Player */}
      {selected?.videoUrl ? (
        <CinematicPlayer
          src={selected.videoUrl}
          title={`${selected.title}${selected.creator ? ` — ${selected.creator}` : ""}`}
          poster={selected.thumb || ""}
          autoPlay={false}
          onEnded={() => {
            // advance to next playable
            const idx = items.findIndex((i) => i.id === selected.id);
            const next = items.slice(idx + 1).find((i) => i.videoUrl);
            if (next) setSelected(next);
          }}
        />
      ) : (
        <Alert severity="warning">No playable source for the selected item.</Alert>
      )}

      {/* Title, creator, description under player for readability */}
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="h5" sx={{ color: "white", fontFamily: "basic-sans", fontWeight: 700 }}>
          {selected?.title || ""}
        </Typography>
        {selected?.creator && (
          <Typography variant="subtitle2" sx={{ color: theme.colors.amber }}>
            by {selected.creator}
          </Typography>
        )}
        {selected?.description && (
          <Typography variant="body2" sx={{ color: "white", opacity: 0.9, mt: 0.5 }}>
            {selected.description}
          </Typography>
        )}
      </Box>

      {/* Playlist grid (click to switch) */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ color: "white", fontFamily: "basic-sans", mb: 1 }}>
          Included videos
        </Typography>
        <Grid container spacing={1.5}>
          {items.map((it) => {
            const isActive = selected?.id === it.id;
            return (
              <Grid item xs={6} sm={4} md={3} key={it.id}>
                <Card
                  onClick={() => setSelected(it)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: theme.colors.mediumPurple,
                    color: "white",
                    borderRadius: "14px",
                    border: `2px solid ${isActive ? theme.colors.green : theme.colors.palePurple}`,
                    overflow: "hidden",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      pt: "56.25%",
                      background: it.thumb
                        ? `url(${it.thumb}) center/cover no-repeat`
                        : `linear-gradient(135deg, ${theme.colors.darkBlue}, ${theme.colors.headerPurple})`,
                    }}
                  />
                  <CardContent sx={{ pb: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "basic-sans", fontWeight: 700 }}
                      noWrap
                      title={it.title}
                    >
                      {it.title}
                    </Typography>
                    {it.creator && (
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        by {it.creator}
                      </Typography>
                    )}
                    {!it.videoUrl && (
                      <Typography variant="caption" sx={{ display: "block", opacity: 0.65, mt: 0.5 }}>
                        Not playable
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Container>
  );
};

export default EventWatchPage;
